import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from 'components/Context/AuthContext';
import { useNavigate } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

/**
 * LoginModal — shared login modal used in TailRiskGlobal (as overlay) and Login page (always-open).
 *
 * Props:
 *   open           {boolean}   — whether the modal overlay is visible
 *   onClose        {function|null} — called when user closes; null = no × button (standalone page mode)
 *   onRequestDemo  {function}  — called when user clicks "request a demo"
 *   next           {string}    — redirect path after successful login (default: '/dashboard')
 */
export default function LoginModal({ open, onClose, onRequestDemo, next = '/summary' }) {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [email,            setEmail]            = useState('');
  const [password,         setPassword]         = useState('');
  const [error,            setError]            = useState('');
  const [loading,          setLoading]          = useState(false);

  // Reset form each time the modal opens
  useEffect(() => {
    if (open) {
      setEmail('');
      setPassword('');
      setError('');
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/login`, { username: email, password });
      if (response.data.token) {
        login(response.data);
        // SSO cookie — non-blocking, failure doesn't break login
        try {
          await axios.post(`${apiUrl}/set_sso_cookie`, {}, {
            params: { token: response.data.token },
            headers: { accept: 'application/json', 'Content-Type': 'application/json' },
            withCredentials: true,
          });
        } catch (err) {
          console.error('SSO request failed', err);
        }
        if (onClose) onClose();
        navigate(next);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError(err.response.data.error || 'Incorrect email or password. Please try again.');
      } else if (status === 403) {
        setError('Access denied. Your account does not have permission to log in here.');
      } else if (status === 429) {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else if (status >= 500) {
        setError('Server error. Please try again later.');
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Login failed (${status ?? 'unknown error'}). Please try again.`);
      }
    }
    setLoading(false);
  };

  const handleRequestDemo = (e) => {
    e.preventDefault();
    handleClose();
    if (onRequestDemo) setTimeout(onRequestDemo, 120);
  };

  return (
    <>
      <div
        className={`trg-overlay${open ? ' open' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div className="trg-modal" style={{ maxWidth: '420px' }}>
          <div className="modal-header">
            <div className="modal-logo">
              <svg width="24" height="17" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <div className="modal-logo-text">
                <div className="modal-logo-main">Tail Risk <span>Global</span></div>
                <div className="modal-logo-sub">Client Portal</div>
              </div>
            </div>
            {onClose && (
              <button className="modal-close" onClick={handleClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 2l14 14M16 2L2 16" />
                </svg>
              </button>
            )}
          </div>

          <div className="modal-body">
            <div className="modal-title">Sign in to your account</div>
            <div className="modal-subtitle">Access your TRG risk dashboard</div>

            {error && <div className="modal-error" style={{ display: 'block' }}>{error}</div>}

            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-field">
                <label className="modal-label" htmlFor="lm-email">Email address</label>
                <input
                  className="modal-input"
                  type="email"
                  id="lm-email"
                  placeholder="you@yourfirm.com"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label" htmlFor="lm-password">Password</label>
                <input
                  className="modal-input"
                  type="password"
                  id="lm-password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                />
                <a href="/forgetPassword" className="modal-forgot">Forgot password?</a>
              </div>
              <button type="submit" className="modal-submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Access Dashboard'}
                {!loading && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 7h10M8 3l4 4-4 4" />
                  </svg>
                )}
              </button>
            </form>

            <div className="modal-divider"></div>
            <div className="modal-footer-note">
              Don&#8217;t have an account?{' '}
              <a href="/createAccount">Create account</a>
              {' '}or{' '}
              <a href="#" onClick={handleRequestDemo}>request a demo</a>
              {' '}to get started.<br />
              Need help?{' '}
              <a href="mailto:info@tailriskglobal.com">info@tailriskglobal.com</a>
              {!onClose && (
                <>
                  <br />
                  <a href="/" style={{ color: 'rgba(255,255,255,0.35)' }}>&#8592; Back to home</a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
